/**
 * Light, dark, or follow the system. The choice is remembered in the browser and applied as
 * `data-theme` on `<html>`; the CSS variables in `theme.css` do the rest.
 */
import { createSignal, type Accessor } from "solid-js";
import { load, save } from "../shared/storage";
import type { CommandSet } from "../shared/commands";
import type { MenuSection } from "../shared/menus";

/** The theme choices. */
export type ThemeMode = "system" | "light" | "dark";

/** Where the choice is kept in the browser. */
const KEY = "developer-app-theme-v1";
/** The order of {@link Theme.cycle}. */
const ORDER: ThemeMode[] = ["system", "light", "dark"];

/** The current theme and a way to cycle it. */
export interface Theme {
  /** The current choice (reactive). */
  mode: Accessor<ThemeMode>;
  /** system → light → dark → system. */
  cycle(): void;
  /** Chooses a mode and remembers it. */
  set(mode: ThemeMode): void;
}

/** Applies the saved theme and returns the controls. */
export function createTheme(): Theme {
  const initial = load<ThemeMode>(KEY, "system");
  const [mode, setMode] = createSignal<ThemeMode>(ORDER.includes(initial) ? initial : "system");
  /** Sets `data-theme` on `<html>` (none means "follow the system"). */
  const apply = (m: ThemeMode) => {
    if (m === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", m);
  };
  apply(mode());
  /** Chooses, remembers and applies a mode. */
  const set = (m: ThemeMode) => {
    setMode(m);
    save(KEY, m);
    apply(m);
  };
  return { mode, set, cycle: () => set(ORDER[(ORDER.indexOf(mode()) + 1) % ORDER.length]!) };
}

/** The theme's shortcut commands. */
export function themeCommands(theme: Theme): CommandSet {
  return { "theme.cycle": () => theme.cycle() };
}

/** The View menu's theme: system, light or dark. */
export function themeMenus(theme: Theme): MenuSection[] {
  const modes = [
    ["system", "System"],
    ["light", "Light"],
    ["dark", "Dark"],
  ] as const;
  return [{ menu: "view", section: "Look", order: 30, entries: () => [{ label: "Theme", items: modes.map(([mode, label]) => ({ label, checked: theme.mode() === mode, action: () => theme.set(mode) })) }] }];
}
